import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useMe } from '../features/auth/useMe';
import { ErrorNotice } from './ErrorNotice';
import { LoadingScreen } from './LoadingScreen';

/**
 * Route guard (ARCHITECTURE_PLAN §10: "session required except /login and
 * /book/:slug"). Bootstraps the session via `GET /api/auth/me`:
 *   - pending        → full-page loading state;
 *   - error          → full-page error with the safe message + requestId and a
 *                      retry (no blank failure state);
 *   - `user === null`→ redirect to /login, remembering where the visitor was
 *                      headed so login can route them back;
 *   - otherwise      → render the guarded subtree.
 */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) {
    return <LoadingScreen />;
  }

  if (me.isError) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md">
          <h1 className="text-lg font-semibold tracking-tight">Could not load your session</h1>
          <ErrorNotice error={me.error} className="mt-3" />
          <button
            type="button"
            onClick={() => {
              void me.refetch();
            }}
            className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (me.data.user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
